import { Children, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { CloudUploadOutlined } from '@mui/icons-material';
import { Box, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import { designTokens } from '../tokens';
import { createFocusRingStyles } from '../interaction';
import { Field, type FieldControlState } from './Field';

const hasContent = (value: ReactNode) => value !== undefined && value !== null && value !== '' && typeof value !== 'boolean';

type AttachmentControlProps = {
  /** 파일을 보관하거나 업로드하는 동작과 오류 처리는 호출자가 소유합니다. */
  onFilesSelected: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  busy?: boolean;
  busyLabel?: ReactNode;
  actionLabel?: string;
  helperText?: ReactNode;
  error?: ReactNode;
  emptyLabel?: ReactNode;
  /** AttachmentItem이나 업무별 첨부 항목을 전달합니다. */
  children?: ReactNode;
  testId?: string;
  inputTestId?: string;
};

export type AttachmentFieldProps = AttachmentControlProps & (
  | { label: ReactNode; required?: boolean; controlId?: string; state?: undefined }
  | { state: FieldControlState; label?: never; required?: never; controlId?: never }
);

function AttachmentControl({
  state, onFilesSelected, accept, multiple = false, disabled = false, readOnly = false,
  busy = false, busyLabel = '파일을 처리하고 있습니다.', actionLabel = '파일을 끌어다 놓거나 선택하세요',
  helperText, error, emptyLabel = '등록된 파일이 없습니다.', children, testId, inputTestId, fieldMessageId,
}: AttachmentControlProps & { state: FieldControlState; fieldMessageId?: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const isReadOnly = readOnly || state.readOnly;
  const unavailable = disabled || state.disabled || isReadOnly || busy;
  const hasAttachments = Children.toArray(children).length > 0;
  const hasHelper = hasContent(helperText);
  const hasError = hasContent(error);
  const helperId = `${state.controlId}-attachment-help`;
  const errorId = `${state.controlId}-attachment-error`;
  const busyId = `${state.controlId}-attachment-status`;
  const describedBy = [fieldMessageId, hasHelper ? helperId : undefined, hasError ? errorId : undefined, busy ? busyId : undefined].filter(Boolean).join(' ') || undefined;
  const receivesFiles = (event: DragEvent<HTMLDivElement>) => event.dataTransfer.types.includes('Files');
  const resetDrag = () => { dragDepth.current = 0; setDragging(false); };

  return <Stack spacing={1} role="group" aria-labelledby={state.labelId} data-pattern="attachment-field">
    <Box
      data-testid={testId}
      onDragEnter={(event) => {
        if (!receivesFiles(event)) return;
        event.preventDefault();
        if (unavailable) return;
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragOver={(event) => {
        if (!receivesFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = unavailable ? 'none' : 'copy';
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
      }}
      onDrop={(event) => {
        if (!receivesFiles(event)) return;
        event.preventDefault();
        resetDrag();
        if (!unavailable && event.dataTransfer.files.length) onFilesSelected(Array.from(event.dataTransfer.files));
      }}
      sx={isReadOnly ? undefined : {
        p: `${designTokens.space.md}px`,
        border: '1px dashed',
        borderColor: hasError || state.invalid ? designTokens.color.danger : dragging && !unavailable ? designTokens.color.action : designTokens.color.strongDivider,
        borderRadius: designTokens.radius.input,
        bgcolor: dragging && !unavailable ? designTokens.color.actionSoft : designTokens.color.subtleSurface,
        '@media (forced-colors: active)': { borderColor: unavailable ? 'GrayText' : dragging ? 'Highlight' : 'CanvasText' },
      }}
    >
      {!isReadOnly ? <>
        <input
          ref={inputRef} id={`${state.controlId}-file-input`} data-testid={inputTestId}
          type="file" hidden multiple={multiple} accept={accept} disabled={unavailable}
          aria-labelledby={state.labelId} aria-describedby={describedBy}
          onChange={(event) => {
            const files = Array.from(event.currentTarget.files ?? []);
            event.currentTarget.value = '';
            if (!unavailable && files.length) onFilesSelected(files);
          }}
        />
        <ButtonBase
          id={state.controlId} type="button" disabled={unavailable}
          aria-labelledby={state.labelId} aria-describedby={describedBy}
          aria-invalid={hasError || state.invalid} aria-busy={busy || undefined}
          disableRipple
          onClick={() => inputRef.current?.click()}
          sx={{
            width: '100%', minWidth: 0, minHeight: designTokens.layout.controlMinHeight, p: `${designTokens.space.xs}px`,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left',
            gap: `${designTokens.layout.listRowGap}px`, color: designTokens.color.primaryText,
            borderRadius: designTokens.radius.input,
            '&:hover': { bgcolor: designTokens.color.hoverSurface },
            '&.Mui-focusVisible, &:focus-visible': createFocusRingStyles({ inset: true }),
            '&.Mui-disabled': { color: designTokens.color.disabledText },
            [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize },
            '@media (hover: none)': { '&:hover': { bgcolor: 'transparent' } },
            '@media (forced-colors: active)': { '&.Mui-disabled': { color: 'GrayText' } },
          }}
        >
          {busy ? <CircularProgress size={designTokens.layout.pageHeaderControlGlyphSize} aria-hidden /> : <CloudUploadOutlined aria-hidden fontSize="small" />}
          <Typography component="span" sx={{ ...designTokens.typography.ui, fontWeight: designTokens.button.fontWeight, overflowWrap: 'anywhere' }}>{actionLabel}</Typography>
        </ButtonBase>
      </> : null}
      {hasHelper ? <Typography component="div" id={helperId} variant="caption" color="text.secondary" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{helperText}</Typography> : null}
      {hasAttachments ? <Stack spacing={0.75} sx={isReadOnly ? undefined : { mt: 1, pt: 1, borderTop: `1px solid ${designTokens.color.divider}` }}>{children}</Stack>
        : isReadOnly ? <Typography component="div" variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{emptyLabel}</Typography> : null}
    </Box>
    {busy ? <Typography component="div" id={busyId} role="status" variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{busyLabel}</Typography> : null}
    {hasError ? <Typography component="div" id={errorId} role="alert" variant="caption" sx={{ color: designTokens.color.dangerText, overflowWrap: 'anywhere' }}>{error}</Typography> : null}
  </Stack>;
}

/** 파일 선택 표면을 공유하고, 업로드·검증·저장 상태는 호출자가 제공합니다. */
export function AttachmentField(props: AttachmentFieldProps) {
  if (props.state) return <AttachmentControl {...props} state={props.state} fieldMessageId={props.state.messageId} />;
  const { label, required, controlId, ...controlProps } = props;
  return <Field.Root controlId={controlId} required={required} disabled={props.disabled} readOnly={props.readOnly} invalid={hasContent(props.error)}>
    <Field.Header><Field.Label>{label}</Field.Label></Field.Header>
    <Field.Control>{(state) => <AttachmentControl {...controlProps} state={state} />}</Field.Control>
  </Field.Root>;
}
